import React, { forwardRef, memo, useImperativeHandle, useRef } from 'react';

import { ChatSuggestedEvent, UnifiedChatRichContextEntry } from '../../../data-access';
import type { NearbyPlace } from '../../../data-access/api/science/openapi/api';
import type { ProjectFilterItem } from '../../../hooks';
import AssistantChatSuggestedEventBlock from '../AssistantChatSuggestedBlocks/AssistantChatSuggestedEventBlock';
import AssistantChatSuggestedTaskBlock from '../AssistantChatSuggestedBlocks/AssistantChatSuggestedTaskBlock';
import AssistantChatSuggestedRoutineBlock from '../AssistantChatSuggestedBlocks/AssistantChatSuggestedRoutineBlock';
import { SuggestedBlockHandle, SuggestedBlockActionsVisibility } from './types';

interface SuggestedBlockRowProps {
  block: ChatSuggestedEvent;
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageCalendar?: (e: ChatSuggestedEvent) => Promise<boolean>;
  onCheckCalendar?: (startTime: string) => void;
  onManageTodo?: (e: ChatSuggestedEvent, board: ProjectFilterItem) => Promise<boolean>;
  nearbyPlaces?: NearbyPlace[];
  richContextEntries?: UnifiedChatRichContextEntry[];
  onDiscard: (eventId: string) => void;
  actionsVisibility: SuggestedBlockActionsVisibility;
  /** Grouped card context: whether the item starts expanded — all items
   * start collapsed so a fresh plan doesn't open with a menu already up. */
  defaultExpanded?: boolean;
}

const TASK_ACTIONS = new Set(['setTask', 'deleteTask']);
const ROUTINE_ACTIONS = new Set(['setRoutine', 'deleteRoutine']);

/**
 * Per-item row for the grouped suggestion list — a thin pass-through to the
 * existing AssistantChatSuggestedEventBlock/TaskBlock/RoutineBlock, just
 * picking the right one and forwarding the accept() ref.
 */
const SuggestedBlockRow = memo(
  forwardRef<SuggestedBlockHandle, SuggestedBlockRowProps>(
    (
      {
        block,
        loadingEventId,
        processedIds,
        onProcessedIdsChange,
        onManageCalendar,
        onCheckCalendar,
        onManageTodo,
        nearbyPlaces,
        richContextEntries,
        onDiscard,
        actionsVisibility,
        defaultExpanded,
      },
      ref
    ) => {
      const innerRef = useRef<SuggestedBlockHandle>(null);
      const isTaskAction = TASK_ACTIONS.has(block.action || '');
      const isRoutineAction = ROUTINE_ACTIONS.has(block.action || '');

      useImperativeHandle(
        ref,
        () => ({
          accept: () => innerRef.current?.accept() ?? Promise.resolve(false),
        }),
        []
      );

      if (isTaskAction) {
        return (
          <AssistantChatSuggestedTaskBlock
            ref={innerRef}
            block={block}
            loadingEventId={loadingEventId}
            processedIds={processedIds}
            onProcessedIdsChange={onProcessedIdsChange}
            onManageTodo={onManageTodo}
            variant="screen"
            onDiscard={onDiscard}
            actionsVisibility={actionsVisibility}
            defaultExpanded={defaultExpanded}
          />
        );
      }

      if (isRoutineAction) {
        return (
          <AssistantChatSuggestedRoutineBlock
            ref={innerRef}
            block={block}
            loadingEventId={loadingEventId}
            processedIds={processedIds}
            onProcessedIdsChange={onProcessedIdsChange}
            onManageCalendar={onManageCalendar}
            variant="screen"
            onDiscard={onDiscard}
            actionsVisibility={actionsVisibility}
            defaultExpanded={defaultExpanded}
          />
        );
      }

      return (
        <AssistantChatSuggestedEventBlock
          ref={innerRef}
          block={block}
          loadingEventId={loadingEventId}
          processedIds={processedIds}
          onProcessedIdsChange={onProcessedIdsChange}
          onCheckCalendar={onCheckCalendar}
          onManageCalendar={onManageCalendar}
          variant="screen"
          nearbyPlaces={nearbyPlaces}
          richContextEntries={richContextEntries}
          onDiscard={onDiscard}
          actionsVisibility={actionsVisibility}
          defaultExpanded={defaultExpanded}
        />
      );
    }
  )
);

export default SuggestedBlockRow;
